import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { QuoteCard, QuoteItem } from '@/components/quick-study-results/QuickStudyOpenEndCard'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { cn } from '@/lib/utils'
import { personaThumbUrl } from '@/utils/personaThumbUrl'

import { StudyDocQuote } from './enrichStudyDocQuote'
import { PortraitMosaicKpiData } from './PortraitMosaicStrip/PortraitMosaicKpi'
import {
  buildPortraitMosaicTiles,
  PORTRAIT_MOSAIC_FALLBACK_AVATAR_CLASS,
  PORTRAIT_MOSAIC_TILE_ASPECT_CLASS,
  PortraitMosaicResponse,
} from './PortraitMosaicStrip/buildPortraitMosaicTiles'
import { PortraitMosaicMode } from './PortraitMosaicStrip/PortraitMosaicStrip'

/** Featured verbatim plus this many cards — D9 bounded digest. */
export const PRINT_DIGEST_QUOTE_CARD_CAP = 4

/** One composed portrait row. Matches the unmeasured mosaic fallback so print is a full row. */
export const PRINT_DIGEST_PORTRAIT_COLUMNS = 8

export interface PrintDigestQuoteSplit<T> {
  featured: T | undefined
  cards: Array<T>
  totalCount: number
  isCapped: boolean
}

/**
 * Voices-band split: first quote is featured, then up to
 * {@link PRINT_DIGEST_QUOTE_CARD_CAP} cards. `isCapped` when anything is omitted.
 */
export function splitPrintDigestQuotes(
  quotes: Array<StudyDocQuote> | undefined,
): PrintDigestQuoteSplit<StudyDocQuote> {
  const list = quotes ?? []
  const featured = list[0]
  const cards = list.slice(1, 1 + PRINT_DIGEST_QUOTE_CARD_CAP)
  const shown = (featured ? 1 : 0) + cards.length

  return {
    featured,
    cards,
    totalCount: list.length,
    isCapped: list.length > shown,
  }
}

/**
 * Gallery split: keep the screen featured pick, then up to
 * {@link PRINT_DIGEST_QUOTE_CARD_CAP} of the remaining cards.
 */
export function splitPrintDigestGalleryItems(
  items: Array<QuoteItem>,
  featuredIndex: number,
): PrintDigestQuoteSplit<QuoteItem> {
  const featured =
    featuredIndex >= 0 && featuredIndex < items.length ? items[featuredIndex] : undefined
  const rest = items.filter((_, index) => index !== featuredIndex)
  const cards = rest.slice(0, PRINT_DIGEST_QUOTE_CARD_CAP)
  const shown = (featured ? 1 : 0) + cards.length

  return {
    featured,
    cards,
    totalCount: items.length,
    isCapped: items.length > shown,
  }
}

interface StudyDocPrintDigestProps {
  mode?: PortraitMosaicMode
  quotes?: Array<StudyDocQuote>
  responses: Array<PortraitMosaicResponse>
  pickerImages?: Record<string, string>
  highlightedPersonaIds?: Array<string>
  kpi?: PortraitMosaicKpiData
  className?: string
}

interface StudyDocQuotesPrintDigestProps {
  items: Array<QuoteItem>
  featuredIndex: number
  className?: string
}

function PrintDigestOverflowCaption(props: { count: number }) {
  const { count } = props
  const { t } = useTranslation('app')

  return (
    <Text
      as="p"
      size="sm"
      color="muted"
      className="font-sans font-normal not-italic normal-case tracking-normal"
      data-testid="study-doc-print-digest-overflow"
    >
      {t('studyDocChrome.verbatimsInInteractiveReport', { count })}
    </Text>
  )
}

function PrintDigestCite(props: { quote: StudyDocQuote }) {
  const { quote } = props
  const { t } = useTranslation('app')
  const attribution = (quote.attribution || quote.name || '').trim()
  const versionLabel = (quote.versionLabel || '').trim()

  if (!attribution && !versionLabel) {
    return null
  }

  return (
    <p
      className="mt-2 font-sans text-sm font-normal not-italic tracking-normal text-muted-foreground"
      data-testid="study-doc-print-digest-cite"
    >
      {attribution || null}
      {versionLabel ? (
        <>
          {attribution ? ' · ' : ''}
          <Trans
            // Same phrasing rule as the live band cite (PortraitMosaicQuoteBlock): a
            // choice question reads "chose X", everything else "ranked X first".
            i18nKey={
              quote.versionLabelKind === 'chose'
                ? 'studyDocChrome.chosePicked'
                : 'studyDocChrome.rankedFirst'
            }
            t={t}
            values={{ label: versionLabel }}
            components={{
              accent: (
                <span className="font-medium not-italic text-[hsl(var(--brand-accent,var(--primary)))]" />
              ),
            }}
          />
        </>
      ) : null}
    </p>
  )
}

function PrintDigestQuote(props: { quote: StudyDocQuote; variant: 'featured' | 'card' }) {
  const { quote, variant } = props
  const isFeatured = variant === 'featured'

  return (
    <div
      data-testid={
        isFeatured ? 'study-doc-print-digest-featured' : 'study-doc-print-digest-card'
      }
    >
      <p
        className={cn(
          'font-sans font-normal not-italic tracking-normal',
          isFeatured
            ? 'text-[1.15rem] leading-[1.45] sm:text-[1.35rem]'
            : 'text-sm leading-relaxed',
        )}
      >
        {quote.text}
      </p>
      <PrintDigestCite quote={quote} />
    </div>
  )
}

function PrintDigestPortraitRow(props: {
  responses: Array<PortraitMosaicResponse>
  pickerImages?: Record<string, string>
  highlightedPersonaIds?: Array<string>
}) {
  const { responses, pickerImages, highlightedPersonaIds } = props

  const tiles = buildPortraitMosaicTiles(responses, {
    highlightedPersonaIds,
    pickerImages,
    columns: PRINT_DIGEST_PORTRAIT_COLUMNS,
  }).slice(0, PRINT_DIGEST_PORTRAIT_COLUMNS)

  if (tiles.length === 0) {
    return null
  }

  return (
    <div
      className="grid w-full gap-0.5"
      style={{
        gridTemplateColumns: `repeat(${PRINT_DIGEST_PORTRAIT_COLUMNS}, minmax(0, 1fr))`,
      }}
      data-testid="study-doc-print-digest-portraits"
      data-columns={PRINT_DIGEST_PORTRAIT_COLUMNS}
    >
      {tiles.map((tile, index) => (
        <div
          key={tile.personaId}
          className={cn('relative overflow-hidden bg-muted', PORTRAIT_MOSAIC_TILE_ASPECT_CLASS)}
          data-testid={`study-doc-print-digest-portrait-${index}`}
          data-persona-id={tile.personaId}
        >
          {tile.imageUrl ? (
            <PrintDigestPortraitImage imageUrl={tile.imageUrl} initials={tile.initials} name={tile.name} />
          ) : (
            <div className="absolute inset-0 h-full w-full">
              <UserAvatar
                initials={tile.initials}
                name={tile.name}
                size="lg"
                className={PORTRAIT_MOSAIC_FALLBACK_AVATAR_CLASS}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// One-shot fallback (finding 7, image compression): try the pre-generated
// 128px thumbnail first; if it 404s (not backfilled for this persona yet),
// retry the original url once before falling back to the initials avatar.
function PrintDigestPortraitImage(props: { imageUrl: string; initials: string; name: string }) {
  const { imageUrl, initials, name } = props

  const [thumbFailed, setThumbFailed] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)

  const thumbUrl = personaThumbUrl(imageUrl, 128)
  const displayImageUrl = thumbFailed || !thumbUrl ? imageUrl : thumbUrl

  if (hasFailed) {
    return (
      <div className="absolute inset-0 h-full w-full">
        <UserAvatar
          initials={initials}
          name={name}
          size="lg"
          className={PORTRAIT_MOSAIC_FALLBACK_AVATAR_CLASS}
        />
      </div>
    )
  }

  return (
    <img
      src={displayImageUrl}
      alt=""
      className="absolute inset-0 h-full w-full object-cover object-top"
      onError={() => {
        if (!thumbFailed && thumbUrl && thumbUrl !== imageUrl) {
          setThumbFailed(true)
          return
        }
        setHasFailed(true)
      }}
    />
  )
}

function PrintDigestKpi(props: { kpi: PortraitMosaicKpiData }) {
  const { kpi } = props
  const { t } = useTranslation('app')

  return (
    <div data-testid="study-doc-print-digest-kpi">
      <Heading
        level={2}
        weight="medium"
        className="font-sans font-medium not-italic normal-case tracking-normal leading-tight"
      >
        <span
          className="text-[3.4rem] font-medium not-italic leading-none text-[hsl(var(--brand-accent,var(--primary)))]"
          data-testid="study-doc-print-digest-kpi-pct"
        >
          {kpi.pct}%
        </span>{' '}
        <span
          className="text-[2.4rem] font-medium not-italic leading-none"
          data-testid="study-doc-print-digest-kpi-label"
        >
          {t('studyDocChrome.choseLabel', { label: kpi.label })}
        </span>
      </Heading>
      <Text
        as="p"
        size="sm"
        color="muted"
        weight="normal"
        className="mt-2 font-sans font-normal not-italic normal-case tracking-normal"
        data-testid="study-doc-print-digest-kpi-subline"
      >
        {t('studyDocChrome.kpiSubline', {
          selected: kpi.count,
          count: kpi.total,
          pct: kpi.pct,
        })}
      </Text>
    </div>
  )
}

/**
 * Screen-hidden, always-mounted print digest (D9). `window.print` does not
 * re-render React, so this block is in the DOM with `hidden print:block`.
 */
export function StudyDocPrintDigest(props: StudyDocPrintDigestProps) {
  const {
    mode,
    quotes,
    responses,
    pickerImages,
    highlightedPersonaIds,
    kpi,
    className,
  } = props

  const effectiveMode = mode ?? 'quotes'
  const isKpiMode = effectiveMode === 'kpi' && Boolean(kpi)
  const quoteSplit = splitPrintDigestQuotes(quotes)

  if (isKpiMode && kpi) {
    return (
      <div
        hidden
        className={cn('hidden print:block', className)}
        data-testid="study-doc-print-digest"
        data-mode="kpi"
      >
        <PrintDigestKpi kpi={kpi} />
      </div>
    )
  }

  const hasQuotes = quoteSplit.totalCount > 0
  const hasPortraits = responses.length > 0
  if (!hasQuotes && !hasPortraits) {
    return null
  }

  return (
    <div
      hidden
      className={cn('hidden print:block space-y-4', className)}
      data-testid="study-doc-print-digest"
      data-mode="quotes"
    >
      {quoteSplit.featured ? (
        <PrintDigestQuote quote={quoteSplit.featured} variant="featured" />
      ) : null}
      {quoteSplit.cards.length > 0 ? (
        <div
          className="flex flex-col gap-3"
          data-testid="study-doc-print-digest-cards"
          data-layout="stacked"
        >
          {quoteSplit.cards.map((quote, index) => (
            <PrintDigestQuote
              key={`${quote.personaId ?? quote.name}-${index}`}
              quote={quote}
              variant="card"
            />
          ))}
        </div>
      ) : null}
      <PrintDigestPortraitRow
        responses={responses}
        pickerImages={pickerImages}
        highlightedPersonaIds={highlightedPersonaIds}
      />
      {quoteSplit.isCapped ? (
        <PrintDigestOverflowCaption count={quoteSplit.totalCount} />
      ) : null}
    </div>
  )
}

/**
 * Quotes-gallery print digest: the featured pick + up to 4 more, stacked as the
 * same `QuoteCard` the screen shows (avatar, name, profile, score, answer,
 * version and segment chips), all at one size. Own wrapper testids so the live
 * gallery queries stay unique.
 */
export function StudyDocQuotesPrintDigest(props: StudyDocQuotesPrintDigestProps) {
  const { items, featuredIndex, className } = props
  const split = splitPrintDigestGalleryItems(items, featuredIndex)

  if (split.totalCount === 0) {
    return null
  }

  const printed = split.featured ? [split.featured, ...split.cards] : split.cards

  return (
    <div
      hidden
      className={cn('hidden print:block space-y-4', className)}
      data-testid="study-doc-print-digest"
      data-mode="gallery"
    >
      <div
        className="flex flex-col gap-3"
        data-testid="study-doc-print-digest-cards"
        data-layout="stacked"
      >
        {printed.map((item) => (
          <div key={item.key} className="break-inside-avoid" data-testid="study-doc-print-digest-card">
            <QuoteCard item={item} documentMode />
          </div>
        ))}
      </div>
      {split.isCapped ? <PrintDigestOverflowCaption count={split.totalCount} /> : null}
    </div>
  )
}
